import * as utils from '@utils/form';
import moment from 'moment';
import { RELATED_CONTENT_TYPE } from '@constants/relatedContentModules';
import { IMAGE_SIZE } from '@api/media/constants';
import { TITLE_RULE, SLUG_RULE } from '@constants';

export const FORM_FIELD = {
  TYPE: {
    name: 'type',
    label: 'Asset type:',
    rules: [{ required: true, message: 'Asset type is required' }],
    serialize: utils.makeRelatedEntitySerializer([{ key: 'contentId', as: 'id' }]),
    deserialize: utils.makeRelatedEntityDeserializer([{ key: 'contentId', as: 'id' }, 'title']),
    deserializeError: utils.deserializeErrorRelatedField,
    miniSiteType: 'all',
  },
  LANGUAGESELECT: {
    name: 'language',
    label: 'Language:',
    miniSiteType: 'all',
  },
  TITLE: {
    name: 'title',
    label: 'Title:',
    rules: [{ required: true, message: 'Title is required' }, TITLE_RULE],
    miniSiteType: 'all',
  },
  SLUG: {
    name: 'slug',
    label: 'Slug:',
    rules: [{ required: true, message: 'Page slug is required' }, SLUG_RULE],
    miniSiteType: 'all',
  },
  SEO_DESCRIPTION: {
    name: 'seoDescription',
    label: 'Description:',
    rules: [{ required: true, message: 'Description is required' }],
    miniSiteType: 'all',
  },
  THUMBNAIL_FEATURE_IMAGE: {
    name: 'thumbnailFeatureImage',
    label: 'Thumbnail image:',
    rules: [{ required: true, message: 'Thumbnail image is required' }],
    size: IMAGE_SIZE['680x453'],
    serialize: utils.imageSerialize,
    deserialize: utils.imageDeserialize,
    deserializeError: utils.deserializeErrorRelatedField,
    miniSiteType: 'all',
  },
  ASSET: {
    name: 'asset',
    label: 'Asset:',
    rules: [{ required: true, message: 'Asset is required' }],
    miniSiteType: 'all',
  },
  GATED_CONTENT: {
    name: 'gatedContent',
    label: 'Gated content',
    miniSiteType: 'all',
  },
  MINISITE_DATE: {
    name: 'date',
    label: 'Asset Date',
    serialize: (field, values) => {
      const value = values[field.name];
      if (value) return { [field.name]: value.toISOString() };
      return null;
    },
    deserialize: (field, serverData) => {
      const value = serverData[field.name];
      if (serverData[field.name]) {
        return {
          [field.name]: moment(Date.parse(value)),
        };
      }
      return {};
    },
  },
  CAMPAIGN: {
    name: 'campaign',
    label: 'Campaign',
    placeholder: 'Add new campaign',
    rules: [{ required: true, message: 'Campaign is required' }],
    serialize: utils.relatedEntitySerializer,
    deserialize: utils.relatedEntityDeserializer,
    deserializeError: utils.deserializeErrorRelatedField,
    miniSiteType: 'all',
  },
  DOWNLOAD_ONLY: {
    name: 'downloadOnly',
    label: 'Download only',
    miniSiteType: 'all',
  },
  MINISITE: {
    name: 'minisite',
    label: 'Mini site:',
    rules: [{ required: true, message: 'Minisite is required' }],
    serialize: utils.relatedEntitySerializer,
    deserialize: utils.relatedEntityDeserializer,
    deserializeError: utils.deserializeErrorRelatedField,
    miniSiteType: 'all',
  },
  RELATED_AGE_GROUP: {
    name: 'relatedAgeGroup',
    type: RELATED_CONTENT_TYPE.AGE_GROUPS,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'kidsathletics',
  },
  RELATED_ACTIVITY_GROUP: {
    name: 'relatedActivityGroup',
    type: RELATED_CONTENT_TYPE.ACTIVITY_GROUP,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'kidsathletics',
  },
  RELATED_ATHLETICS_SKILL: {
    name: 'relatedAthleticsSkill',
    type: RELATED_CONTENT_TYPE.ATHLETICS_SKILL,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'kidsathletics',
  },
  RELATED_FUNDAMENTAL_SKILL: {
    name: 'relatedFundamentalSkill',
    type: RELATED_CONTENT_TYPE.FUNDAMENTAL_SKILLS,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'kidsathletics',
  },
  RELATED_KIDS_ATHLETICS_VALUE: {
    name: 'relatedKidsAthleticsValue',
    type: RELATED_CONTENT_TYPE.KIDS_ATHLETICS_VALUES,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'kidsathletics',
  },
  RELATED_RESOURCE_TYPE: {
    name: 'relatedResourceType',
    type: RELATED_CONTENT_TYPE.RESOURCE_TYPE,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'kidsathletics',
  },
  RELATED_CONTENT_TRACK: {
    name: 'relatedContentTrack',
    type: RELATED_CONTENT_TYPE.OREGON_CONTENT_TRACK,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'oregonyoutheducation',
  },
  RELATED_SUGGESTED_GRADE_LEVEL: {
    name: 'relatedSuggestedGradeLevel',
    type: RELATED_CONTENT_TYPE.OREGON_SUGGESTED_GRADE_LEVEL,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'oregonyoutheducation',
  },
  RELATED_SUBJECT_AREA: {
    name: 'relatedSubjectArea',
    type: RELATED_CONTENT_TYPE.OREGON_SUBJECT_AREA,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'oregonyoutheducation',
  },
  RELATED_TRACK_FIELD_FOCUS: {
    name: 'relatedTrackFieldFocus',
    type: RELATED_CONTENT_TYPE.OREGON_TRACK_FIELD_FOCUS,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'oregonyoutheducation',
  },
  RELATED_ACTIVITY_SPACE: {
    name: 'relatedActivitySpace',
    type: RELATED_CONTENT_TYPE.OREGON_ACTIVITY_SPACE,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'oregonyoutheducation',
  },
  RELATED_ACTIVITY_TYPE: {
    name: 'relatedActivityType',
    type: RELATED_CONTENT_TYPE.OREGON_ACTIVITY_TYPE,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'oregonyoutheducation',
  },
  RELATED_MEDICAL_CARE: {
    name: 'relatedMedicalCare',
    type: RELATED_CONTENT_TYPE.MEDICAL_CARE,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'worldacademyforendurancemedicine',
  },
  RELATED_RISK_MITIGATION: {
    name: 'relatedRiskMitigation',
    type: RELATED_CONTENT_TYPE.RISK_MITIGATION,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'worldacademyforendurancemedicine',
  },
  RELATED_TRAINING_AND_PERFORMANCE: {
    name: 'relatedTrainingAndPerformance',
    type: RELATED_CONTENT_TYPE.TRAINING_AND_PERFORMANCE,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'worldacademyforendurancemedicine',
  },
  RELATED_ENVIRONMENT: {
    name: 'relatedEnvironment',
    type: RELATED_CONTENT_TYPE.ENVIRONMENT,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'worldacademyforendurancemedicine',
  },
  RELATED_SPECIAL_POPULATIONS: {
    name: 'relatedSpecialPopulations',
    type: RELATED_CONTENT_TYPE.SPECIAL_POPULATION,
    serialize: utils.multilingualRelatedContentIdSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
    miniSiteType: 'worldacademyforendurancemedicine',
  },
};

export const FORM_FIELDS = Object.values(FORM_FIELD);

export const DEFAULT_VALUES = {};

export const FORM_VALUES_TO_WATCH = [FORM_FIELD.TYPE.name];
